import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getSiteProcessTypes,
  getSiteLocations,
  getSiteDetailLocations,
} from '../../../lib/site-metadata';

const STORAGE_KEY = 'safetyhub:site-master-expanded';

/**
 * 사업장 관리 탭의 "대상 구성 마스터" 섹션 펼침/접힘 상태 훅
 *
 * - 첫 방문: 펼침 (마스터 등록 안내)
 * - 사용자가 토글한 상태는 localStorage에 저장되어 다음 방문에 유지
 * - 사이트 무관 글로벌 선호 (한 번 접으면 모든 사이트에서 접힘 유지)
 */
export function useMasterExpanded(): [boolean, () => void] {
  const [expanded, setExpanded] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      return saved === null ? true : saved === 'true';
    } catch {
      return true;
    }
  });

  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // localStorage 비활성 환경 (시크릿 모드 등) — silent fail
      }
      return next;
    });
  };

  return [expanded, toggle];
}

/**
 * 마스터 섹션 카운트 (공종/공정, 장소, 위치)
 *
 * 자식 컴포넌트(SiteProcessTypesSection, SiteLocationsSection, SiteDetailLocationsSection)
 * 와 동일 queryKey를 사용하므로 TanStack Query cache가 공유되어 중복 fetch 없음.
 */
export function useMasterCounts(companyId: string, siteId: string, isManagementTabActive: boolean) {
  const enabled = !!companyId && !!siteId && isManagementTabActive;

  const { data: processTypes = [] } = useQuery({
    queryKey: ['site-process-types', companyId, siteId],
    queryFn: () => getSiteProcessTypes(companyId, siteId),
    enabled,
  });
  const { data: locations = [] } = useQuery({
    queryKey: ['site-locations', companyId, siteId],
    queryFn: () => getSiteLocations(companyId, siteId),
    enabled,
  });
  const { data: detailLocations = [] } = useQuery({
    queryKey: ['site-detail-locations', companyId, siteId],
    queryFn: () => getSiteDetailLocations(companyId, siteId),
    enabled,
  });

  const processCount = processTypes.filter(pt => pt.parentId === null).length;

  return {
    processCount,
    locationCount: locations.length,
    detailLocationCount: detailLocations.length,
  };
}
